import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { animate, m } from "framer-motion";
import React from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
const FADE_IN_DELAY = 0.8;
const StyledChip = /*#__PURE__*/_styled(m.div).withConfig({
  componentId: "sc-sf-vrasl-0"
})`
  ${{
  "position": "absolute",
  "left": "0",
  "top": "0",
  "borderRadius": "2px",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))",
  "paddingLeft": "0.8rem",
  "paddingRight": "0.8rem",
  "paddingTop": "0.4rem",
  "paddingBottom": "0.4rem",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))"
}}
  ${{
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
}}
`;
var _StyledStyledChip = _styled4(StyledChip).withConfig({
  componentId: "sc-sf-vrasl-1"
})({
  "bottom": "0",
  "top": "auto",
  "height": "auto"
});
const backgroundStyles = {
  default: {
    "--tw-gradient-from": "rgb(92 92 92 / 0.6) var(--tw-gradient-from-position)",
    "--tw-gradient-to": "rgb(92 92 92 / 0.1) var(--tw-gradient-to-position)",
    "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
  },
  highlighted: {
    "--tw-gradient-from": "#6C7061 var(--tw-gradient-from-position)",
    "--tw-gradient-to": "rgb(108 112 97 / 0) var(--tw-gradient-to-position)",
    "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)"
  }
} as const;
const fragment = graphql(`
  fragment GraphicStatsGraphicBar on statsGraphic_statsGraphicBar_BlockType {
    name: statsGraphicBarName
    value: statsGraphicBarValue
    highlighted: statsGraphicBarHighlighted
    tag: statsGraphicBarTag
  }
`);
type Props = FragmentType<typeof fragment> & {
  max: number;
  reveal: boolean;
  index: number;
};
export type GraphicStatsGraphicBarViewProps = {
  name?: string | null;
  value?: number | null;
  highlighted?: boolean | null;
  tag?: string | null;
  max: number;
  reveal: boolean;
  index: number;
};
export function GraphicStatsGraphicBarView({
  name,
  value,
  highlighted,
  tag,
  max,
  reveal,
  index
}: GraphicStatsGraphicBarViewProps) {
  const numericValue = value ?? 0;
  const percentage = numericValue / max * 100;
  const ref = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (!reveal) return;
    const node = ref.current;
    if (!node) return;
    const controls = animate(0, numericValue, {
      delay: index * 0.08 + FADE_IN_DELAY,
      duration: 1.2,
      onUpdate(value) {
        node.textContent = `${+Number(value).toFixed(0)}%`;
      }
    });
    return () => controls.stop();
  }, [reveal, index, numericValue]);
  return <_StyledMDiv $_css={[{
    "position": "relative",
    "height": "8rem",
    "width": "100%",
    "borderRadius": "2px",
    "backgroundImage": "linear-gradient(to right, var(--tw-gradient-stops))",
    "--tw-gradient-from": "rgb(92 92 92 / 0) var(--tw-gradient-from-position)",
    "--tw-gradient-to": "#5C5C5C var(--tw-gradient-to-position)",
    "--tw-gradient-stops": "var(--tw-gradient-from), var(--tw-gradient-to)",
    "@media (min-width: 1024px)": {
      "height": "10.8rem"
    }
  }, backgroundStyles[highlighted ? "highlighted" : "default"]]}>
      <_StyledMSpan animate={{
      opacity: reveal ? 1 : 0
    }} transition={{
      delay: index * 0.12 + FADE_IN_DELAY + 0.2
    }}>
        {name}
      </_StyledMSpan>

      <_StyledStyledChip animate={{
      left: reveal ? `${percentage}%` : 0,
      opacity: reveal ? 1 : 0
    }} transition={{
      delay: index * 0.12 + FADE_IN_DELAY,
      duration: 1.2,
      ease: "easeOut"
    }}>
        <span ref={ref}>+{numericValue}%</span>
      </_StyledStyledChip>

      <_StyledMDiv2 animate={{
      left: reveal ? `${percentage}%` : 0,
      opacity: reveal ? 1 : 0
    }} transition={{
      delay: index * 0.12 + FADE_IN_DELAY,
      duration: 1.2,
      ease: "easeOut"
    }} />

      {tag && <StyledChip>{tag}</StyledChip>}
    </_StyledMDiv>;
}
export function GraphicStatsGraphicBar(props: Props) {
  const {
    max,
    reveal,
    index,
    ...rest
  } = props;
  const {
    name,
    value,
    highlighted,
    tag
  } = useFragment(fragment, rest);
  return <GraphicStatsGraphicBarView name={name} value={value} highlighted={highlighted} tag={tag} max={max} reveal={reveal} index={index} />;
}
var _StyledMDiv = _styled2(m.div).withConfig({
  componentId: "sc-sf-vrasl-2"
})`${p => p.$_css}`;
var _StyledMSpan = _styled3(m.span).withConfig({
  componentId: "sc-sf-vrasl-3"
})({
  "position": "absolute",
  "bottom": "0",
  "left": "0",
  "padding": "0.8rem",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))",
  "opacity": "0",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledMDiv2 = /*#__PURE__*/_styled5(m.div).withConfig({
  displayName: "graphic-bar___StyledMDiv2",
  componentId: "sc-1oot641-0"
})({
  "position": "absolute",
  "height": "100%",
  "width": "0.1rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(189 191 181 / var(--tw-bg-opacity))"
});